import { api } from './client';
import type { SheetsSettings, SlaSettings, TelegramBoundChat, TelegramSettings, WorkspaceSettings } from './types';

export function getSlaSettings(): Promise<{ section: string; settings: SlaSettings }> {
  return api.get('/api/v1/settings/sla');
}
export function patchSlaSettings(body: Partial<SlaSettings>): Promise<{ section: string; settings: SlaSettings }> {
  return api.patch('/api/v1/settings/sla', body);
}

export function getTelegramSettings(): Promise<{ section: string; settings: TelegramSettings }> {
  return api.get('/api/v1/settings/telegram');
}
/** `bot_token: string` saves+encrypts a new token, `bot_token: null` clears it. */
export function patchTelegramSettings(
  body: Partial<TelegramSettings> & { bot_token?: string | null },
): Promise<{ section: string; settings: TelegramSettings }> {
  return api.patch('/api/v1/settings/telegram', body);
}

export interface TelegramTestResult {
  ok: true;
  bot_name: string | null;
  bot_username: string | null;
  verified_at: string;
}
/** "Проверить связь": calls getMe with the just-saved token. */
export function testTelegramConnection(): Promise<TelegramTestResult> {
  return api.post('/api/v1/settings/telegram/test');
}

export interface TelegramMode {
  real: boolean;
  using_workspace_token: boolean;
}
export function getTelegramMode(): Promise<TelegramMode> {
  return api.get('/api/v1/settings/telegram/mode');
}

export function getTelegramChats(): Promise<{ chats: TelegramBoundChat[] }> {
  return api.get('/api/v1/settings/telegram/chats');
}
export function unbindTelegramChat(kind: 'leads_chat' | 'alerts_chat' | 'team_chat'): Promise<{ ok: true }> {
  return api.post(`/api/v1/settings/telegram/chats/${kind}/unbind`);
}

export interface TelegramTestMessageResult {
  ok: true;
  chat_id: number;
  message_id: number;
}
export function sendTelegramTestMessage(body: {
  chat_role?: 'leads' | 'alerts' | 'team';
  chat_id?: number;
  text?: string;
}): Promise<TelegramTestMessageResult> {
  return api.post('/api/v1/settings/telegram/test-message', body);
}

/** Requests a `/start <code>` or `/bind <code>` one-time code (existing endpoint, not settings-owned). */
export function createTelegramLinkCode(
  kind: 'leads_chat' | 'alerts_chat' | 'team_chat',
): Promise<{ code: string; command: string; expires_at: string }> {
  return api.post('/api/v1/me/telegram/link-code', { kind });
}

export function getSheetsSettings(): Promise<{ section: string; settings: SheetsSettings }> {
  return api.get('/api/v1/settings/sheets');
}
/**
 * `spreadsheet_url_or_id`: pasted link or bare id, parsed server-side.
 * `service_account_json`: raw key file content (`null` clears it).
 */
export function patchSheetsSettings(
  body: Partial<SheetsSettings> & { spreadsheet_url_or_id?: string | null; service_account_json?: string | null },
): Promise<{ section: string; settings: SheetsSettings }> {
  return api.patch('/api/v1/settings/sheets', body);
}

export interface SheetsTestAccessResult {
  ok: true;
  title: string;
  verified_at: string;
}
export function testSheetsAccess(): Promise<SheetsTestAccessResult> {
  return api.post('/api/v1/settings/sheets/test-access');
}

export interface SheetsTestRowResult {
  ok: true;
  row: number;
}
export function writeSheetsTestRow(): Promise<SheetsTestRowResult> {
  return api.post('/api/v1/settings/sheets/test-row');
}

export function getWorkspaceSettings(): Promise<{ section: string; settings: WorkspaceSettings }> {
  return api.get('/api/v1/settings/workspace');
}
export function patchWorkspaceSettings(
  body: Partial<WorkspaceSettings>,
): Promise<{ section: string; settings: WorkspaceSettings }> {
  return api.patch('/api/v1/settings/workspace', body);
}
